import React from "react";
import MessageBubble from "../MessageBubble";
import "../../css/messageutils.css";

// Helper: robust equality for ObjectId/string/null
const eqId = (a, b) => a != null && b != null && String(a) === String(b);

// Helper: safe date creation
const toDate = (value) => {
  const d = value ? new Date(value) : new Date();
  return isNaN(d.getTime()) ? new Date() : d;
};

// Helper: readable duration "Xm Ys"
function formatDuration(seconds = 0) {
  const s = Number(seconds) || 0;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}m ${secs}s`;
}

export function renderMessagesWithDates(messages, userId) {
  // Guard: messages must be an array
  if (!Array.isArray(messages)) return null;

  const elements = [];
  let lastDate = "";
  const renderedCallIds = new Set();

  messages.forEach((msg, i) => {
    // Guard against malformed entries
    if (!msg) return;

    const timestamp = toDate(msg.timestamp);
    const msgDate = timestamp.toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Date separator
    if (msgDate !== lastDate) {
      elements.push(
        <div key={`d-${msgDate}-${timestamp.getTime()}`} className="date-separator">
          {msgDate}
        </div>
      );
      lastDate = msgDate;
    }

    const type = msg.type || "text";

    // Call messages rendered as left/right bubbles (WhatsApp-style)
    if (type === "call") {
      // Build a stable unique key
      const fromStr = String(msg.from || "");
      const toStr = String(msg.to || "");
      const baseTs = String(msg.timestamp || timestamp.getTime());
      const statusStr = String(msg.status || "");
      const callKey = msg._id || `${fromStr}-${toStr}-${baseTs}-${statusStr}`;

      // Avoid duplicate rendering: single record per call
      if (renderedCallIds.has(callKey)) return;
      renderedCallIds.add(callKey);

      const time = timestamp.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      const isMine = eqId(msg.from, userId);
      const icon = msg.callType === "video" ? "📹" : "📞";
      const status = msg.status || "";
      let label = "";

      // Normalize status text
      switch (status) {
        case "started":
          label = isMine ? `${icon} Calling...` : `${icon} Incoming ${msg.callType || "voice"} call`;
          break;
        case "ended":
          label = `${icon} Call ended (${formatDuration(msg.duration)})`;
          break;
        case "missed":
          label = isMine
            ? `${icon} Outgoing call (missed)`
            : `${icon} Missed ${msg.callType || "voice"} call`;
          break;
        case "rejected":
          label = isMine
            ? `${icon} Call rejected`
            : `${icon} Rejected ${msg.callType || "voice"} call`;
          break;
        // Optional: handle legacy "outgoing"
        case "outgoing":
          label = `${icon} Outgoing ${msg.callType || "voice"} call`;
          break;
        default:
          // Unknown status: skip rendering to avoid broken UI
          label = "";
      }

      if (label) {
        elements.push(
          <div
            key={`c-${callKey}`}
            className={`call-bubble ${isMine ? "mine" : "theirs"}`}
          >
            <div className="call-label">{label}</div>
            <div className="call-time">{time}</div>
          </div>
        );
      }

      return; // processed call; continue
    }

    // Regular chat messages
    elements.push(
      <MessageBubble
        key={`m-${msg._id || `${i}-${timestamp.getTime()}`}`}
        msg={msg}
        self={eqId(msg.from, userId)}
      />
    );
  });

  return elements;
}